import { orderIntent, orderRetryable } from './operator.js';

// These are the two registration formats emitted by Composer (pool and pilot).
// Names are a display hint, never an identity or an authorization boundary.
const POOL_NAME = /^Composer (.+) ([a-f0-9]{8}) ([a-z0-9][a-z0-9_-]*)$/;
const PILOT_NAME = /^(.+) - Composer revisor Core V\d+$/;

export function machineGroups(rows) {
    const hints = rows.map(row => ({ row, pool: POOL_NAME.exec(row.machine.name ?? '') }));
    const pools = new Map();
    for (const { row, pool } of hints) {
        if (!pool || row.machine.user_id == null) continue;
        const host = `${row.machine.user_id}:${pool[1]}`;
        if (!pools.has(host)) pools.set(host, new Set());
        pools.get(host).add(pool[2]);
    }
    const groups = new Map();
    for (const { row, pool } of hints) {
        const pilot = PILOT_NAME.exec(row.machine.name ?? '');
        const host = pool?.[1] ?? pilot?.[1];
        const matches = host && pools.get(`${row.machine.user_id}:${host}`);
        const poolId = pool?.[2] ?? (matches?.size === 1 ? [...matches][0] : null);
        const hasPool = poolId && row.machine.user_id != null;
        const key = hasPool
            ? `pool:${row.machine.user_id}:${host}:${poolId}`
            : `machine:${row.machine.id}`;
        if (!groups.has(key)) groups.set(key, {
            key, name: hasPool ? host : (row.machine.name || `Equipo #${row.machine.id}`),
            poolId: hasPool ? poolId : null, rows: [],
        });
        groups.get(key).rows.push(row);
    }
    return [...groups.values()];
}

export function projectGroups(rows, repositories, projects) {
    const byRepository = new Map(repositories.map(repo => [repo.id, repo]));
    const byProject = new Map(projects.map(project => [project.id, project]));
    const groups = new Map();
    for (const row of rows) {
        const projectId = byRepository.get(row.seat.repository_id)?.project_id ?? null;
        // Missing catalog access is unknown, not a project shared by every unknown repo.
        const key = projectId == null ? `repository:${row.seat.repository_id}` : `project:${projectId}`;
        if (!groups.has(key)) groups.set(key, {
            key, id: projectId,
            name: projectId == null ? 'Proyecto sin identificar' : (byProject.get(projectId)?.name ?? `Proyecto #${projectId}`),
            rows: [],
        });
        groups.get(key).rows.push(row);
    }
    return [...groups.values()];
}

export function agentName(row, repositories = []) {
    const repo = repositories.find(item => item.id === row.seat.repository_id);
    const provider = { claude_code: 'Claude Code', codex: 'Codex' }[row.seat.provider] ?? row.seat.provider;
    const role = row.seat.role === 'agent_master' ? 'Revisor' : 'Desarrollador';
    return `${role} · ${provider} · ${repo?.name ?? `Repositorio #${row.seat.repository_id}`}`;
}

// CON TRABAJO TOMADO, no «trabajando»: son dos cosas y el panel solo puede
// probar la primera. Una corrida abierta la declaró el propio agente; una ronda
// reservada la registró Core. Que el proceso local esté vivo no viaja por
// ninguna ruta, así que el número no lo afirma.
//
// `reviewWork` es el mapa de rondas reservadas por puesto (utils/activity.js).
// Sin él —sin `events:read`— se cuentan solo las corridas, que es lo que esta
// cuenta puede ver.
export function fleetCounts(rows, reviewWork) {
    const reserved = rows.filter(row => {
        if (row.work.live) return false;
        const review = reviewWork?.get(row.position_id);
        return !!review && !review.expired;
    }).length;

    return {
        total: rows.length,
        connected: rows.filter(row => row.presence.state === 'activa').length,
        enabled: rows.filter(row => row.seat.current && row.control.desired_state === 'habilitado' && row.control.settled).length,
        working: rows.filter(row => row.work.live).length + reserved,
        running: rows.filter(row => row.work.live).length,
        reviewing: reserved,
    };
}

export function orderCandidates(rows, verb) {
    const desired = verb === 'habilitar' ? 'habilitado' : 'pausado';
    const seen = new Set();
    return rows.filter(row => {
        if (seen.has(row.position_id)) return false;
        seen.add(row.position_id);
        return row.seat.current === true && Number.isInteger(row.seat.revision)
            && Number.isInteger(row.control?.revision)
            // Core's previous confirmation survives a paused Composer restart.
            // A human may renew authorization; Core still checks revisions and live orders.
            && (verb === 'habilitar' || row.control.desired_state !== desired || !row.control.settled);
    });
}

export function batchIntents(rows, verb, reason, now) {
    if (!['habilitar', 'pausar_tomas'].includes(verb)) throw new Error('Esta acción no admite grupos.');
    return orderCandidates(rows, verb).map(row => ({
        positionId: row.position_id, row, state: 'pending',
        body: orderIntent(null, { row, verb, reason, now }),
    }));
}

// Keep each uncertain request intact. A conflict needs a new human decision;
// it must not silently replace the revision the operator actually reviewed.
export async function sendBatch(entries, send, onProgress = () => {}) {
    const results = [...entries];
    for (let index = 0; index < results.length; index++) {
        const entry = results[index];
        if (!['pending', 'uncertain'].includes(entry.state)) continue;
        try {
            const response = await send(entry.positionId, entry.body);
            results[index] = { ...entry, state: response.sent ? 'registered' : 'simulated', error: null };
        } catch (error) {
            results[index] = { ...entry, state: orderRetryable(error) ? 'uncertain' : 'failed', error: error.message, status: error.status };
        }
        onProgress([...results]);
        // Stop on auth failure; retry pending entries only after the operator restores access.
        // Network failures also pause the batch rather than flooding a broken connection.
        if (results[index].state === 'uncertain'
            || results[index].state === 'failed' && ![409, 422].includes(results[index].status)) break;
    }
    return results;
}

// Bulk actions must never mistake the first API page for an entire project.
export async function collectPages(readPage, idOf = item => item.id) {
    const items = [];
    const ids = new Set();
    let first;
    let total;
    do {
        const page = await readPage({ limit: 100, offset: items.length });
        first ??= page;
        total ??= page.pagination?.total;
        if (!Number.isSafeInteger(total) || total < 0 || total > 10000
            || page.pagination?.total !== total || !Array.isArray(page.items)) {
            throw new Error('El listado cambió o está incompleto. Actualiza antes de autorizar un grupo.');
        }
        if (items.length < total && page.items.length === 0) throw new Error('No se pudo cargar el grupo completo. Actualiza para continuar.');
        for (const item of page.items) {
            const id = idOf(item);
            if (id == null || ids.has(id)) throw new Error('El listado cambió mientras se consultaba. Actualiza para continuar.');
            ids.add(id);
            items.push(item);
        }
    } while (items.length < total);
    if (items.length !== total) throw new Error('El listado cambió. Actualiza para continuar.');
    return { ...first, items, pagination: { total, limit: total, offset: 0 } };
}
